import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Typography,
  useTheme,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Stack,
  LinearProgress,
} from '@mui/material';
import { Refresh, Download, TrendingUp, TrendingDown, AccountBalance, Receipt, AttachFile } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { UserContext } from '../../components/shared/useUser';
import {
  getCashManagementClient,
  CashMovementDto,
  CashBalanceDto,
  CashSummaryDto,
  formatCurrency,
  getMovementTypeColor,
  getTillStatusColor,
} from '../../app/services/api/cashManagement';
import useLogError from '../../hooks/useLogError';

const CashManagement: React.FC = () => {
  const { t } = useTranslation('cashManagement');
  const theme = useTheme();
  const navigate = useNavigate();
  const { selectedStore } = useContext(UserContext);
  const { logError } = useLogError();

  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState<CashBalanceDto | null>(null);
  const [movements, setMovements] = useState<CashMovementDto[]>([]);
  const [summary, setSummary] = useState<CashSummaryDto | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const cashClient = getCashManagementClient();

  useEffect(() => {
    if (selectedStore?.storeNumber) {
      loadData();
    }
  }, [selectedStore?.storeNumber]);

  const loadData = async () => {
    if (!selectedStore?.storeNumber) return;

    setLoading(true);
    try {
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

      const [balanceData, movementsData, summaryData] = await Promise.all([
        cashClient.getCashBalance(selectedStore.storeNumber),
        cashClient.getCashMovements(selectedStore.storeNumber, startOfDay.toISOString(), endOfDay.toISOString()),
        cashClient.getCashSummary(selectedStore.storeNumber, today.toISOString()),
      ]);

      setBalance(balanceData);
      setMovements(movementsData);
      setSummary(summaryData);
    } catch (error) {
      logError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = (documentId: number) => {
    // Navigate to document library with CCTV filter
    navigate(`/documents/${documentId}`);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pagedMovements = movements.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}
          md={9}>
          <PageTitle>{t('pageTitle', 'Cash Overview')}</PageTitle>
        </Grid>

        <Grid item
          xs={12}
          md={3}
          sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
          <Button
            size='lg'
            variant='secondary'
            startIcon={<Refresh />}
            onClick={loadData}
          >
            {t('actionRefresh', 'Refresh')}
          </Button>
          <Button
            size='lg'
            variant='primary'
            startIcon={<Download />}
            onClick={() => {/* Export logic */ }}
          >
            {t('actionExport', 'Export')}
          </Button>
        </Grid>

        {loading && (
          <Grid item
            xs={12}>
            <LinearProgress />
          </Grid>
        )}

        {/* Current Balance */}
        <Grid item
          xs={12}
          md={3}>
          <Section style={{ height: '100%', bgcolor: theme.palette.success.light + '15' }}>
            <Box sx={{ p: 5 }}>
              <Stack spacing={1}>
                <Stack direction="row"
                  justifyContent="space-between"
                  alignItems="center">
                  <Typography variant="textSM"
                    color="text.secondary"
                    fontWeight={600}>
                    {t('currentBalance', 'Current Balance')}
                  </Typography>
                  <AccountBalance sx={{ color: theme.palette.success.main }} />
                </Stack>
                <Typography variant="displaySM"
                  fontWeight={700}
                  color={theme.palette.success.main}>
                  {balance ? formatCurrency(balance.currentBalance, balance.currencyCode) : '—'}
                </Typography>
                {balance && balance.variance !== 0 && (
                  <Chip
                    label={t('varianceLabel', 'Variance: {{amount}}', { amount: formatCurrency(Math.abs(balance.variance), balance.currencyCode) })}
                    size="small"
                    color={balance.variance > 0 ? 'warning' : 'error'}
                    sx={{ width: 'fit-content' }}
                  />
                )}
              </Stack>
            </Box>
          </Section>
        </Grid>

        {/* Today's Sales */}
        <Grid item
          xs={12}
          md={3}>
          <Section style={{ height: '100%' }}>
            <Box sx={{ p: 5 }}>
              <Stack spacing={1}>
                <Stack direction="row"
                  justifyContent="space-between"
                  alignItems="center">
                  <Typography variant="textSM"
                    color="text.secondary"
                    fontWeight={600}>
                    {t('todaysSales', 'Today\'s Sales')}
                  </Typography>
                  <TrendingUp sx={{ color: theme.palette.primary.main }} />
                </Stack>
                <Typography variant="displaySM"
                  fontWeight={700}>
                  {summary ? formatCurrency(summary.totalSales, summary.currencyCode) : '—'}
                </Typography>
                <Typography variant="textSM"
                  color="text.secondary">
                  {summary && t('cashCardBreakdown', 'Cash: {{cash}} | Card: {{card}}', { cash: formatCurrency(summary.totalCashSales, summary.currencyCode), card: formatCurrency(summary.totalCardSales, summary.currencyCode) })}
                </Typography>
              </Stack>
            </Box>
          </Section>
        </Grid>

        {/* Bank Deposits */}
        <Grid item
          xs={12}
          md={3}>
          <Section style={{ height: '100%' }}>
            <Box sx={{ p: 5 }}>
              <Stack spacing={1}>
                <Stack direction="row"
                  justifyContent="space-between"
                  alignItems="center">
                  <Typography variant="textSM"
                    color="text.secondary"
                    fontWeight={600}>
                    {t('bankDeposits', 'Bank Deposits')}
                  </Typography>
                  <TrendingDown sx={{ color: theme.palette.info.main }} />
                </Stack>
                <Typography variant="displaySM"
                  fontWeight={700}>
                  {summary ? formatCurrency(summary.bankDeposits, summary.currencyCode) : '—'}
                </Typography>
                <Typography variant="textSM"
                  color="text.secondary">
                  {t('todaysBankingActivity', 'Today\'s banking activity')}
                </Typography>
              </Stack>
            </Box>
          </Section>
        </Grid>

        {/* Refunds */}
        <Grid item
          xs={12}
          md={3}>
          <Section style={{ height: '100%' }}>
            <Box sx={{ p: 5 }}>
              <Stack spacing={1}>
                <Stack direction="row"
                  justifyContent="space-between"
                  alignItems="center">
                  <Typography variant="textSM"
                    color="text.secondary"
                    fontWeight={600}>
                    {t('refunds', 'Refunds')}
                  </Typography>
                  <Receipt sx={{ color: theme.palette.warning.main }} />
                </Stack>
                <Typography variant="displaySM"
                  fontWeight={700}>
                  {summary ? formatCurrency(summary.totalRefunds, summary.currencyCode) : '—'}
                </Typography>
                <Typography variant="textSM"
                  color="text.secondary">
                  {t('processedToday', 'Processed today')}
                </Typography>
              </Stack>
            </Box>
          </Section>
        </Grid>

        <Grid item
          xs={12}>
          <Section>
            <TableHeader>{t('tillStatus', 'Till Status')}</TableHeader>
            <Box sx={{ p: 5, pt: 0 }}>
              <Grid container
                spacing={2}>
                {balance?.tillBalances.map((till) => (
                  <Grid item
                    xs={12}
                    sm={6}
                    md={4}
                    key={till.tillNumber}>
                    <Card variant="outlined">
                      <CardContent>
                        <Stack spacing={1}>
                          <Stack direction="row"
                            justifyContent="space-between"
                            alignItems="center">
                            <Typography variant="textMD"
                              fontWeight={600}>
                              {till.tillNumber}
                            </Typography>
                            <Chip
                              label={till.status}
                              size="small"
                              color={getTillStatusColor(till.status) as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                            />
                          </Stack>
                          <Typography variant="textLG"
                            fontWeight={700}>
                            {formatCurrency(till.balance, balance.currencyCode)}
                          </Typography>
                          {till.lastReconciliationUtc && (
                            <Typography variant="textSM"
                              color="text.secondary">
                              {t('lastReconciled', 'Last reconciled: {{datetime}}', { datetime: new Date(till.lastReconciliationUtc).toLocaleString() })}
                            </Typography>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Section>
        </Grid>

        <Grid item
          xs={12}>
          <Section>
            <TableHeader>{t('recentCashMovements', 'Recent Cash Movements')}</TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('columnTime', 'Time')}</TableCell>
                    <TableCell>{t('columnType', 'Type')}</TableCell>
                    <TableCell>{t('columnTill', 'Till')}</TableCell>
                    <TableCell>{t('columnAmount', 'Amount')}</TableCell>
                    <TableCell>{t('columnUser', 'User')}</TableCell>
                    <TableCell>{t('columnReference', 'Reference')}</TableCell>
                    <TableCell align="center">{t('columnDocument', 'Document')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedMovements.map((movement) => (
                    <TableRow key={movement.id}
                      hover>
                      <TableCell>
                        <Typography variant="textSM">
                          {new Date(movement.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={movement.movementType}
                          size="small"
                          color={getMovementTypeColor(movement.movementType) as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="textSM">{movement.tillNumber || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="textSM"
                          fontWeight={600}
                          color={['Sale', 'CashIn', 'FloatIn'].includes(movement.movementType) ? 'success.main' : 'error.main'}
                        >
                          {['Sale', 'CashIn', 'FloatIn'].includes(movement.movementType) ? '+' : '-'}
                          {formatCurrency(movement.amount, movement.currencyCode)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="textSM">{movement.username}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="textSM"
                          color="text.secondary">
                          {movement.reference || movement.transactionId || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {movement.documentId ? (
                          <Tooltip title={t('viewCctvFootage', 'View related CCTV footage')}>
                            <IconButton size="small"
                              onClick={() => handleViewDocument(movement.documentId!)}>
                              <AttachFile fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="textSM"
                            color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      count={movements.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Section>
        </Grid>
      </Grid>
    </PageArea>
  );
};

export default CashManagement;
